/* global nv, NO_DATA_AVAILABLE*/
import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { LegendsAndGraph, LegendsList, useSelection } from "./Legends";

const _defaultMargin = { top: 30, right: 50, bottom: 30, left: 70 };

const doesNothing = () => {};

const WrapperDIV = styled.div`
  position: relative;
  height: ${(props) => (props.height ? `${props.height}px` : "auto")};
  & > svg {
    width: 100%;
    height: ${(props) => (props.height ? `${props.height}px` : "auto")};
    text.clickable tspan,
    text.clickable {
      font-size: 11px;
      text-decoration: underline;
    }
    .clickable {
      cursor: pointer;
    }
    .nvd3 .nv-multibarHorizontal .nv-groups {
      .inside-labels-overlay {
        text {
        fill: var(--Text-Primary);
        font-family: "AllianceNo2-SemiBold";
        }
      }
      text {
        dominant-baseline: middle;
        &.embeded-left {
          text-anchor: end;
          fill: white;
        }
      }
    }
  }
`;

export const formats = {
  noDecimals: d3.format(".0f"),
  oneDecimal: d3.format(".1f"),
  twoDecimals: d3.format(".2f"),
  threeDecimals: d3.format(".3f"),
};

const atLeast = min => value => min > value ? min : value;

const waitFor = (delay) =>
  new Promise( resolve => {
    window.setTimeout(resolve, delay)
  })

const defineRectRoundedClip = (target, {id, radius, left, right}) => {
  const group = d3.select(target.node().parentNode);
  let definition = group.select(`clipPath#${id}`)
  if (definition.empty()) {
    definition = group.append('clipPath')
      .attr('id',id )
      .classed('roundedCorners', true)
      .append('rect')
      .attr('rx', radius)
      .attr('ry', radius);
  }
  definition
    .attr('height', target.attr('height'))
    .attr('width',
      atLeast(radius)(
        parseInt(target.attr('width')
      )+ (left && right ? 0 : radius))
    )
    .attr('x', left ? 0: -radius );
}

const clearRoundCorners = main =>
  main.selectAll('.roundedCorners').remove();

const applyRoundCorners = (selection, {radius=5, stacked=false, id: chartId}) =>
  selection.each( function updateRoundCornersOnItem(d, index){
    const current = d3.select(this);
    const {left, right} = stacked === false ? {left: true, right: true} : d;
    if (left !== true && right !== true) {
      current.attr('clip-path', null);
      return
    }
    const id =`${chartId}-rounded-shape-${index}${left ?'-left':''}${right ?'-right':''}`
    current.call(defineRectRoundedClip, {id, left, right, radius, stacked});
    current.attr('clip-path', `url(#${id})`)
  });

const markFirstAndLast = () => {
  let firsts = null;
  let lasts = null;
  const initIfWerent = d =>{
    firsts = firsts || d.values.map( () => null );
    lasts = lasts ||d.values.map( () => null );
  }
  const consider = ({values=[]}) => {
    values.forEach(value => {value.left = false; value.right = false;});
    firsts = firsts.map( (current, index) =>
      current === null && values[index].value > 0 && values[index] || current
    )
    lasts = lasts.map( (current, index) =>
      values[index].value > 0 ? values[index] : current
    )
  }
  const apply = () => {
    firsts.forEach( current => current && Object.assign(current, {left: true}))
    lasts.forEach( current => current && Object.assign(current, {right: true}))
  }

  return function evalGroup(d, index, all) {
    initIfWerent(d);
    if (d.rounded !== false) {
      consider(d);
    }
    if (index >= (all.length - 1) ) {
      apply();
    }
  }
}

const useOrClone = (from, selector, source) => {
  const target = from.select(selector);
  return target.node() === null
    ? source.clone()
    : target.attr("d", source.attr("d"));
};

const callSetRoundCorners = (main, options) =>
  main.call(clearRoundCorners)
    .selectAll(".nv-groups .nv-group .nv-bar rect")
    .call(applyRoundCorners, options)


const callDrawLimits = (main) => {
  const target = d3.select(main.node());
  const yDomainPath = target.select(".nv-y .domain");
  if (yDomainPath.node() === null) {
    return main;
  }
  const xDomainPath = target.select(".nv-x .domain");
  const { width } = yDomainPath.node().getBBox();
  const { height } = xDomainPath.node().getBBox();
  useOrClone(target, ".nv-x .limit", xDomainPath)
    .classed("domain", false)
    .classed("limit", true)
    .style("transform", `translate(${width}px, 0)`);
  useOrClone(target, ".nv-y .limit", yDomainPath)
    .classed("domain", false)
    .classed("limit", true)
    .style("transform", `translate(0, ${-height}px)`);
};

const NVHorizontalBarChart = ({
  className,
  items,
  height=undefined,
  margin = _defaultMargin,
  animationDuration = 200,
  tooltipValueFormatter = undefined,
  valueUnits = "",
  valueFormat = formats.oneDecimal,
  showMaxMin = true,
  onClick = undefined,
  showValues = false,
  showBorder = false,
  showColors = false,
  showControls = false,
  showRoundCorners = false,
  roundCornerRadius = 5,
  showLegend = true,
  stacked = true,
  valuePadding = undefined,
  yAxisLabel = undefined,
  id,
  fixedYRange,
  labelInsideBar = undefined,
}) => {
  const wrapper = useRef(null);
  const canvas = useRef(null);
  const detailsTimeoutRef = useRef(null);
  const animationTimeoutRef = useRef(null);
  const chartRef = useRef(null);
  const tooltipFormatterRef = useRef(null);
  tooltipFormatterRef.current = tooltipValueFormatter === undefined
    ? (value) => `${formats.oneDecimal(value)} ${valueUnits}`
    : tooltipValueFormatter;
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const labelInsideBarRef = useRef(labelInsideBar);
  labelInsideBarRef.current = labelInsideBar;

  const render = (target) => {
    nv.addGraph(function () {
      const chart = nv.models
        .multiBarHorizontalChart()
        .margin(margin)
        .x(function (d) {
          return d.label;
        })
        .y(function (d) {
          return d.value;
        })
        .stacked(stacked)
        .showControls(showControls)
        .showLegend(showLegend)
        .showValues(showValues)
        .noData(NO_DATA_AVAILABLE);
      chart.tooltip.valueFormatter(
        tooltipValueFormatter === undefined
          ? (value) => `${formats.oneDecimal(value)} ${valueUnits}`
          : tooltipValueFormatter
      );
      if (valuePadding) {
        chart.valuePadding(valuePadding);
      }
      chart.yAxis
        .showMaxMin(showMaxMin)
        .ticks(7)
        .tickFormat(
          valueFormat !== undefined
            ? valueFormat
            : (value) => `${formats.oneDecimal(value)} ${valueUnits}`
        );
      if(fixedYRange){
        chart.forceY(fixedYRange);
      }
      if (yAxisLabel !== undefined) {
        chart.yAxis.axisLabel(yAxisLabel)
      }
      function calcPosition(_d) {
        const height = chart.xAxis.rangeBand();
        return `${height / 2}px`;
      }
      function calcEmbedded(d) {
        const { width } = this.getBoundingClientRect();
        const { width: barWidth } =
          this.previousElementSibling.getBoundingClientRect();
        const [_, totalWidth] = chart.yAxis.range();
        return (d.embedded = width + barWidth > totalWidth);
      }
      const renderValues =
        showValues === true
          ? (main) => {
              main
                .selectAll(".nv-bar text")
                .text((d) => valueFormat(d.value))
                .attr("dy", calcPosition)
                .classed("embeded-left", calcEmbedded)
                .attr("dx", (d) => (d.embedded ? "-0.5em" : "0.5em"))
                .attr("x", (d) => chart.yScale()(d.value));
            }
          : () => {};
      const main = d3.select(canvas.current);
      main
        .datum(items)
        .transition()
        .duration(animationDuration)
        .call(chart);
      d3.select(canvas.current).call(renderValues);
      // Detection of mouseleave is not needed

      const adjustRoundCorners =
        (showRoundCorners === true)
          ? callSetRoundCorners
          : doesNothing

      const drawLimits =
        showBorder === true
          ? callDrawLimits
          : function (d) {
              return d;
            };
      const enableClick = (onClick !== undefined)
        ? (selection, onClick) => {
          const handleBarClick = function () {
            const [data] = d3.select(this).data();
            chart.tooltip.hidden(true);
            onClick(data.label);
          };
          const handleTextClick = function () {
            const [label] = d3.select(this).data();
            chart.tooltip.hidden(true);
            onClick(label);
          };;
          main.selectAll(".nv-x .nv-axis").style("pointer-events", "all");
          main
            .selectAll(".nv-bar rect")
            .on("click", handleBarClick)
            .classed("clickable", true);
          main.selectAll(".nv-axis.nv-x text")
            .on('click', handleTextClick)
            .classed("clickable", true);
        } : doesNothing;

      // Props inside this method are static.
      chart.details = () => {
        if (showValues) {
          main.call(renderValues);
        }
        if (showBorder) {
          main.call(drawLimits);
        }

        if (showColors) {
          main.selectAll("rect").attr("fill", (d) => d.color);
        }
        if (showRoundCorners) {
          main.selectAll(".nv-groups .nv-group").each(markFirstAndLast());
          main.call(adjustRoundCorners, {
            radius: roundCornerRadius,
            stacked,
            id,
          });
        }

        if (labelInsideBarRef.current) {
          main.selectAll(".nv-groups .nv-group").each(function (series) {
            
            if (series.key !== labelInsideBarRef.current) return;

            const group = d3.select(this);
            let labelsOverlay = group.select("g.inside-labels-overlay");
            if (labelsOverlay.empty()) {
              labelsOverlay = group.append("g").attr("class", "inside-labels-overlay");
            }

            const labelData = [];
            group.selectAll("g.nv-bar").each(function (d) {
              const bar = d3.select(this);
              const rectWidthAttr = bar.select("rect").attr("width");
              const rectWidth = rectWidthAttr ? parseFloat(rectWidthAttr) : 0;
              if (rectWidth === 0) return;

              const transform = bar.attr("transform");
              const match = transform && transform.match(/translate\(([^,]+),([^)]+)\)/);
              if (!match) return;

              const barX = parseFloat(match[1]);
              const barY = parseFloat(match[2]);
              const rectHeight = parseFloat(bar.select("rect").attr("height") || 0);
              const formattedValue = valueFormat ? valueFormat(d.value) : d.value;

              labelData.push({
                key: d.label,
                text: rectWidth > 60
                  ? `${String(formattedValue).replace(/\s/g, "")} ${series.key}`
                  : series.key,
                x: barX + Math.max(0, rectWidth - 10),
                y: barY + rectHeight / 2,
              });
            });

            labelsOverlay.selectAll("text.inside-label")
              .data(labelData, (item) => item.key)
              .join(
                (enter) => enter.append("text").attr("class", "inside-label"),
                (update) => update,
                (exit) => exit.remove()
              )
              .text((item) => item.text)
              .attr("x", (item) => item.x)
              .attr("y", (item) => item.y)
              .attr("dy", "0.32em")
              .attr("text-anchor", "end")
              .style("pointer-events", "none")
              .style("font-size", "11px");
          });
        }

        if (onClick) {
          main.call(enableClick, onClick);
        }
      };

      chart.dispatch.on("renderEnd", function () {
        chart.tooltip.valueFormatter(tooltipFormatterRef.current);
        if (!chart.tooltip.hidden()) {
          const tooltipData = chart.tooltip.data();
          if (tooltipData && tooltipData.series && tooltipData.value !== undefined) {
            const label = tooltipData.value;
            const series = Array.isArray(tooltipData.series) ? tooltipData.series : [tooltipData.series];
            const updatedSeries = series.map(s => {
              const seriesData = itemsRef.current.find(item => item.key === s.key);
              if (!seriesData) return s;
              const point = seriesData.values.find(v => v.label === label);
              return point ? { ...s, value: point.value } : s;
            });
            const updated = { ...tooltipData, series: updatedSeries };
            chart.tooltip.data(updated);
            const node = chart.tooltip.node();
            if (node) {
              const html = chart.tooltip.contentGenerator()(updated, node);
              if (html) node.innerHTML = html;
            }
          }
        }
        chart.details()
      });
      chartRef.current = chart;
      return chart;
    });
  };
  useEffect(() => {
    const chart = chartRef.current;
    if (chart !== null ) {
      const main = d3.select(canvas.current);
      main.datum(items);
      refresh(50);
      return ;
    }
    const target = wrapper.current;
    return items ? render(target) : () => {};
  }, [JSON.stringify(items), stacked, chartRef.current]);

  useEffect(() => {
    const chart = chartRef.current;
    return () => {
      console.log("nv-lineChart destroy");
      if (chart !== null) {
        chart.tooltip.hidden(true);
      }
    };
  }, []);

  useEffect(() => {
    const target = wrapper.current;

    if (!target) return;

    let timeoutId;

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        refresh(500);
      }, 500);
    });
    resizeObserver.observe(target);
    return () => {
      resizeObserver.unobserve(target);
      clearTimeout(timeoutId);
    };
  }, []);

  function refresh(delay) {
    const chart = chartRef.current;
    if (chart) {
      chart.update();

      if (animationTimeoutRef.current)
        clearTimeout(animationTimeoutRef.current);

      animationTimeoutRef.current = setTimeout(() => {
        chart.duration(animationDuration);
      }, 250);

      if (detailsTimeoutRef.current) clearTimeout(detailsTimeoutRef.current);
      detailsTimeoutRef.current = setTimeout(() => {
        chart.details();
      }, animationDuration + delay);
    }
  }

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current)
        clearTimeout(animationTimeoutRef.current);
      if (detailsTimeoutRef.current) clearTimeout(detailsTimeoutRef.current);
    };
  }, []);

  return (
    <WrapperDIV
      height={height}
      ref={wrapper}
      className={className}
    >
      <svg ref={canvas}></svg>
    </WrapperDIV>
  );
};

const takeLongerSeriesValuesLength = (higher, {values=[]}) =>
  higher > values.length ? higher : values.length;

const countSeriesIn = (series) =>
  series.reduce(takeLongerSeriesValuesLength, 0)

const calcHeight = ({height=undefined, barHeight=55, items=[], margin=_defaultMargin}) =>
  height !== undefined ? height : 
  barHeight !== undefined ? (countSeriesIn(items) * barHeight) + (margin.top + margin.bottom) :
  undefined

export const calcBarHeight = length =>
  length < 6 ? 55 :
  length < 10 ? 45 :
  length < 20 ? 35 :
  length < 30 ? 30 :
  length < 40 ? 20 :
  15

const LegendsAndNVHorizontalBarChart = ({
  items, onClick, prependLegends=[], hideLegends=false, stacked=true, title, labelInsideBar, ...rest
}) => {
  const [selection, doToggle] = useSelection(items);
  const selectionSet = new Set(selection.map(({key}) => key));
  const labelInsideBarDislay = labelInsideBar && selectionSet.has(labelInsideBar) ? labelInsideBar : undefined;
  return (
    <LegendsAndGraph className="horizontal-bars-wrapper">
      {hideLegends === true ? null : (
        <LegendsList
          className="horizontal-bars-legend"
          prepend={prependLegends}
          items={selection}
          doToggle={doToggle}
          onLegendClick={onClick}
        />
      )}
      {title ? <h6>{title}</h6> : null}
      <div className="horizontal-bars">
        <NVHorizontalBarChart
          height={calcHeight({ items: selection, ...rest })}
          items={selection}
          {...rest}
          showLegend={false}
          onClick={onClick}
          stacked={stacked}
          labelInsideBar={labelInsideBarDislay}
        />
      </div>
    </LegendsAndGraph>
  );
};

export default LegendsAndNVHorizontalBarChart;
